import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, Chip } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MicrospecDataTable, { MicrospecDataTableColumn } from '../../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { deleteDocumentById } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { PROJECTS_COLLECTION } from '../../../constants/collectionPaths';

const ProjectsManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const { translate } = useMicrospecThemeLanguageContext().localizationProvider;
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { firestore } = cloudProvider;
    const db = firestore?.instance;
    const t = translate;
    const [refreshKey, setRefreshKey] = useState(0);

    const columns: MicrospecDataTableColumn[] = [
        {
            field: 'name',
            headerName: t('project.name'),
            sortable: true,
            minWidth: 220,
            align: 'left',
            render: (value: string | { [locale: string]: string }) => {
                if (typeof value === 'string') return value;
                return translate(value);
            },
        },
        {
            field: 'description',
            headerName: t('project.description'),
            sortable: false,
            minWidth: 280,
            align: 'left',
            render: (value: string | { [locale: string]: string }) => {
                if (!value) return '-';
                if (typeof value === 'string') return value;
                return translate(value);
            },
        },
        {
            field: 'mediaUrls',
            headerName: t('project.imagesCount'),
            sortable: false,
            minWidth: 120,
            align: 'center',
            render: (value: string[]) => (Array.isArray(value) ? value.length : 0).toString(),
        },
        {
            field: 'order',
            headerName: t('project.order'),
            sortable: true,
            minWidth: 100,
            align: 'center',
        },
        {
            field: 'is_published',
            headerName: t('project.published'),
            sortable: true,
            minWidth: 130,
            align: 'center',
            render: (value: boolean) => (
                <Chip
                    label={value ? t('project.published') : t('project.draft')}
                    color={value ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                />
            ),
        },
    ];

    const handleDelete = async (row: any) => {
        if (!db) return;
        if (window.confirm(t('project.confirmDelete'))) {
            try {
                await deleteDocumentById(db, PROJECTS_COLLECTION, row.id);
                setRefreshKey(prev => prev + 1);
            } catch (err) {
                console.error('Error deleting project:', err);
                alert(t('project.deleteFailed'));
            }
        }
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <MicrospecDataTable
                key={refreshKey}
                collection={PROJECTS_COLLECTION}
                columns={columns}
                title={t('common.projects')}
                subtitle={t('project.manageDescription')}
                searchable={true}
                exportable={true}
                refreshable={true}
                pageSize={25}
                maxHeight={700}
                onRowClick={(row: any) => navigate(`/manager/projects/update/${row.id}`)}
                actions={[
                    {
                        icon: <EditIcon />,
                        label: t('common.edit'),
                        onClick: (row: any) => navigate(`/manager/projects/update/${row.id}`),
                    },
                    {
                        icon: <DeleteIcon />,
                        label: t('common.delete'),
                        onClick: handleDelete,
                    },
                ]}
                globalActions={[
                    {
                        icon: <AddIcon />,
                        label: t('common.addProject'),
                        onClick: () => navigate('/manager/projects/add'),
                        variant: 'contained',
                        color: 'primary',
                    },
                ]}
            />
        </Box>
    );
};

export default ProjectsManagementPage;
